<template>
  <div :class="['aodn', className]">
    <button
      v-if="!today"
      type="button"
      class="aodn__reset"
      @click="emit('reset-to-today')"
    >
      Reset to today
    </button>

    <span class="aodn__label">{{ labelText }}</span>

    <button
      type="button"
      class="aodn__date-btn"
      :class="{ 'aodn__date-btn--custom': !today }"
      :aria-label="dateButtonAriaLabel"
      aria-haspopup="dialog"
      @click="emit('open-date-picker')"
    >
      <svg
        class="aodn__date-btn-icon"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M15.833 3.333H4.167c-.92 0-1.667.747-1.667 1.667v11.667c0 .92.746 1.666 1.667 1.666h11.666c.92 0 1.667-.746 1.667-1.666V5c0-.92-.746-1.667-1.667-1.667ZM13.333 1.667V5M6.667 1.667V5M2.5 8.333h15"
          stroke="currentColor"
          stroke-width="1.67"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span>{{ dateButtonText }}</span>
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue";
import dayjs from "dayjs";

const props = defineProps({
  /** When true, shows the default “As of today” label; when false, shows “As of” plus custom-date styling. */
  today: {
    type: Boolean,
    default: true,
  },
  /** ISO date (YYYY-MM-DD) displayed in the date button. */
  asOfDate: {
    type: String,
    default: "",
  },
  /** Overrides the default “As of today” / “As of” label with a fixed label (e.g. “Show version as of”). */
  label: {
    type: String,
    default: "",
  },
  /** When set, replaces the formatted date on the button while `today` is true (e.g. “Today”). */
  todayText: {
    type: String,
    default: "",
  },
  /** Optional passthrough class hook for the root element. */
  className: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["reset-to-today", "open-date-picker"]);

const labelText = computed(() => {
  if (props.label) return props.label;
  return props.today ? "As of today" : "As of";
});

const formattedAsOfDate = computed(() =>
  props.asOfDate ? dayjs(props.asOfDate).format("MMM D, YYYY") : ""
);

const dateButtonText = computed(() =>
  props.today && props.todayText ? props.todayText : formattedAsOfDate.value
);

const dateButtonAriaLabel = computed(() => {
  if (props.label) {
    return dateButtonText.value
      ? `${props.label}, ${dateButtonText.value}. Open date picker`
      : `${props.label}. Open date picker`;
  }
  if (props.today) {
    return formattedAsOfDate.value
      ? `As of today, ${formattedAsOfDate.value}. Open date picker`
      : "As of today. Open date picker";
  }
  return formattedAsOfDate.value
    ? `As of ${formattedAsOfDate.value}. Open date picker`
    : "As of custom date. Open date picker";
});
</script>

<style scoped>
.aodn {
  display: flex;
  align-items: center;
  gap: 12px;
}

.aodn__reset {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #175cd3;
  text-decoration: underline;
  white-space: nowrap;
}

.aodn__reset:hover {
  color: #1450b0;
}

.aodn__reset:focus-visible {
  outline: 2px solid #175cd3;
  outline-offset: 2px;
  border-radius: 2px;
}

.aodn__reset:active {
  color: #0d3a80;
}

.aodn__label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  white-space: nowrap;
}

.aodn__date-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
  white-space: nowrap;
}

.aodn__date-btn:hover {
  background: #f9fafb;
  border-color: #98a2b3;
}

.aodn__date-btn:focus-visible {
  outline: 2px solid #175cd3;
  outline-offset: 2px;
}

.aodn__date-btn:active {
  background: #f2f4f7;
  border-color: #667085;
}

.aodn__date-btn--custom {
  background: #eff8ff;
  border-color: #b2ddff;
  color: #175cd3;
}

.aodn__date-btn--custom:hover {
  background: #d4ecff;
  border-color: #84caff;
}

.aodn__date-btn--custom:focus-visible {
  outline: 2px solid #175cd3;
  outline-offset: 2px;
}

.aodn__date-btn--custom:active {
  background: #b9e0ff;
  border-color: #53b1fd;
}

.aodn__date-btn-icon {
  flex-shrink: 0;
  color: #175cd3;
}
</style>
